import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import UserAvatar from '../components/user/UserAvatar';
import { createSharedAppreciation } from '../services/appreciationService';

const AppreciationSuccess = () => {
  const location = useLocation();
  const { recipients, message, companyValues } = location.state || { recipients: [], message: '', companyValues: [] };
  const [copyText, setCopyText] = useState('Copy');
  const [isCopied, setIsCopied] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const linkCreatedRef = useRef(false);

  // Generate shareable link (only once)
  useEffect(() => {
    const generateLink = async () => {
      // Prevent duplicate creation
      if (linkCreatedRef.current) return;

      try {
        const recipientUsernames = recipients.map(r => r.username);
        const data = await createSharedAppreciation(recipientUsernames, message, companyValues || []);
        setShareableLink(data.url);
        linkCreatedRef.current = true;
      } catch (error) {
        console.error('Failed to generate shareable link:', error);
        setShareableLink('');
      }
    };

    if (recipients.length > 0 && message && !linkCreatedRef.current) {
      generateLink();
    }
  }, [recipients, message, companyValues]);

  // Function to copy link to clipboard with text update
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareableLink);
    
    // Set to "Copied" state
    setCopyText('Copied');
    setIsCopied(true);
    
    // Reset after 10 seconds
    setTimeout(() => {
      setCopyText('Copy');
      setIsCopied(false);
    }, 10000);
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold text-gray-900 mb-4">Appreciation sent! 💜</h1>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-medium text-gray-900">Recipients</h2>
        </div>
        <div className="px-6 py-5 flex flex-wrap gap-4">
          {recipients.map((recipient) => (
            <div key={recipient.id} className="flex flex-col items-center">
              <div className="mb-2">
                <UserAvatar user={recipient} size="large" />
              </div>
              <p className="text-sm text-center font-medium">
                {recipient.full_name || recipient.username}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-medium text-gray-900">Message</h2>
        </div>
        <div className="px-6 py-5">
          <p className="text-lg text-gray-800">{message}</p>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Share the appreciation</h2>
        <div className="flex items-center border rounded-md overflow-hidden">
          <div className="flex-grow p-4 bg-white overflow-x-auto whitespace-nowrap">
            <span className="text-blue-600">{shareableLink}</span>
          </div>
          <button 
            onClick={copyToClipboard}
            className={`px-6 py-4 font-medium border-l transition-colors ${
              isCopied 
                ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            disabled={isCopied}
          >
            {copyText}
          </button>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Keep it up</h2>
        <Link
          to="/send-appreciation"
          className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-4 rounded-md"
        >
          Send more appreciation
        </Link>
      </div>
    </div>
  );
};

export default AppreciationSuccess;